import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import ExtraLessonTimeEditModal from './ExtraLessonTimeEditModal';

/**
 * Справочник временных интервалов для доп. занятий. Плоский список start–end,
 * без нумерации по «урокам». Дубликаты времени допустимы (на одно и то же
 * время могут быть разные занятия в разных кабинетах).
 */
const URL_SCHEMA = { archive: '0', edit: '' };

// Из «HH:MM:SS» делаем «HH:MM» для отображения
function shortTime(t) {
  return (t || '').slice(0, 5);
}

export default function ExtraLessonTimesPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('extra.times', 'write');
  const canDelete = can('extra.times', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoad(true); setError(null);
    api.get('/api/extra-lesson-times')
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoad(false));
  };
  useEffect(reload, []);

  const filtered = (params.archive === '1') ? rows : rows.filter((r) => !r.archive);

  const onDelete = async (t) => {
    if (!canDelete) return;
    if (!confirm(`Удалить интервал ${shortTime(t.timeStart)}–${shortTime(t.timeEnd)}?`)) return;
    try {
      await api.delete('/api/extra-lesson-times/' + t.id);
      setRows((prev) => prev.filter((r) => r.id !== t.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Доп. занятия', 'Временные интервалы']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Временные интервалы {!loading && <span className="muted">· {filtered.length}</span>}
          </span>
          <select
            value={params.archive || '0'}
            onChange={(e) => setParams({ archive: e.target.value })}
            style={{
              marginLeft: 14, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="0">Активные</option>
            <option value="1">Все, включая архив</option>
          </select>
          <span style={{ marginLeft: 14, fontSize: 12, color: 'var(--fg-muted)', maxWidth: 540 }}>
            Используются при создании занятия и при расстановке расписания.
          </span>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить интервал
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : filtered.length === 0 ? (
            <div className="ref-empty">Интервалы ещё не созданы. Добавьте первый.</div>
          ) : (
            <div className="elt-grid">
              {filtered.map((t) => (
                <div
                  key={t.id}
                  className={'elt-card' + (t.archive ? ' elt-card--archived' : '')}
                  onClick={() => canWrite && setParams({ edit: String(t.id) })}
                  style={{ cursor: canWrite ? 'pointer' : 'default' }}
                >
                  <div className="elt-card__time mono">
                    <span>{shortTime(t.timeStart)}</span>
                    <span className="elt-card__sep">—</span>
                    <span>{shortTime(t.timeEnd)}</span>
                  </div>
                  {t.archive && <span className="pay-st pay-st--mute">в архиве</span>}
                  {(canWrite || canDelete) && (
                    <div className="ref-row-actions" onClick={(e) => e.stopPropagation()}>
                      {canWrite && (
                        <button onClick={() => setParams({ edit: String(t.id) })} title="Редактировать">
                          <Icon name="edit" size={14} />
                        </button>
                      )}
                      {canDelete && (
                        <button className="is-danger" onClick={() => onDelete(t)} title="Удалить">
                          <Icon name="x" size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {editing && (
        <ExtraLessonTimeEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          time={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(t) => {
            setRows((prev) => {
              const i = prev.findIndex((r) => r.id === t.id);
              if (i >= 0) { const n = prev.slice(); n[i] = t; return n; }
              const next = [...prev, t];
              next.sort((a, b) => (a.archive - b.archive) || a.timeStart.localeCompare(b.timeStart));
              return next;
            });
          }}
        />
      )}
    </>
  );
}
